export type ApInvoiceSkuMatchMethod =
  | "printed"
  | "aml"
  | "description"
  | "purchase_order"
  | "unmapped"
  | "aml_mismatch";

const OUR_PART_NUMBER_PATTERN = /your part number:\s*([A-Za-z0-9][A-Za-z0-9\-_/\.]+)/i;

function normalizePartToken(value: string): string {
  return value.trim().toLowerCase().replace(/[\s\-_/]/g, "");
}

function ensureVendorCatalogFromMpn(input: {
  ourPartNumber: string;
  mpn: string;
  vendorPartNumber: string;
  legacySku: string;
}): string {
  if (input.vendorPartNumber !== "" || input.mpn === "" || input.ourPartNumber === "") {
    return input.vendorPartNumber;
  }
  if (input.mpn.toLowerCase() === input.ourPartNumber.toLowerCase()) {
    return input.vendorPartNumber;
  }
  if (input.legacySku !== "" && input.mpn.toLowerCase() === input.legacySku.toLowerCase()) {
    return input.mpn;
  }
  if (
    input.legacySku !== "" &&
    normalizePartToken(input.mpn) === normalizePartToken(input.legacySku)
  ) {
    return input.mpn;
  }
  if (input.legacySku === "") {
    return input.mpn;
  }
  return input.vendorPartNumber;
}

export function parseOurPartNumberFromDescription(description: string | null | undefined): string | null {
  const text = (description ?? "").trim();
  if (text === "") return null;
  const match = text.match(OUR_PART_NUMBER_PATTERN);
  return match?.[1]?.trim().replace(/[.,;:]+$/, "") ?? null;
}

const MANUFACTURING_LABOR_SKU_PREFIX = /^88-025-/i;

export function isManufacturingLaborSku(sku: string | null | undefined): boolean {
  const trimmed = (sku ?? "").trim();
  if (trimmed === "") return false;
  return MANUFACTURING_LABOR_SKU_PREFIX.test(trimmed) || trimmed === "123456";
}

export function parseFinishedGoodSkuFromLaborDescription(
  description: string | null | undefined,
): string | null {
  const text = (description ?? "").trim();
  if (text === "" || !/manufacturing labor/i.test(text)) return null;
  const segments = text
    .split("|")
    .map((segment) => segment.trim())
    .filter((segment) => segment !== "");
  if (segments.length < 2) return null;
  const candidate = segments[0] ?? "";
  if (candidate === "" || /manufacturing labor/i.test(candidate) || /\s/.test(candidate)) {
    return null;
  }
  return candidate;
}

export function hydrateApInvoiceExtractedPartFields(line: {
  sku: string;
  description: string | null;
  extracted_our_part_number?: string | null;
  extracted_mpn?: string | null;
  extracted_vendor_part_number?: string | null;
  extracted_part_number?: string | null;
  finished_good_sku?: string | null;
}): {
  extracted_our_part_number: string | null;
  extracted_mpn: string | null;
  extracted_vendor_part_number: string | null;
  extracted_part_number: string | null;
  finished_good_sku: string | null;
} {
  const legacySku = (line.sku ?? "").trim();
  const ourPart = (line.extracted_our_part_number ?? "").trim();
  let mpn = (line.extracted_mpn ?? "").trim();
  let vendorPart = (line.extracted_vendor_part_number ?? "").trim();
  const legacy = (line.extracted_part_number ?? "").trim();
  const descriptionOur = parseOurPartNumberFromDescription(line.description);
  const resolvedOur = ourPart !== "" ? ourPart : descriptionOur ?? "";
  const effectiveSku = resolvedOur !== "" ? resolvedOur : legacySku;
  const isLabor = isManufacturingLaborSku(effectiveSku);
  const explicitFinishedGood = (line.finished_good_sku ?? "").trim();
  const resolvedFinishedGood =
    explicitFinishedGood !== ""
      ? explicitFinishedGood
      : isLabor
        ? (parseFinishedGoodSkuFromLaborDescription(line.description) ?? "")
        : "";

  if (!isLabor && mpn === "" && vendorPart === "" && legacy !== "") {
    if (resolvedOur !== "" && legacy.toLowerCase() !== resolvedOur.toLowerCase()) {
      vendorPart = legacy;
      mpn = legacy;
    } else {
      mpn = legacy;
      vendorPart = legacy;
    }
  }

  if (!isLabor && mpn === "" && vendorPart === "" && legacySku !== "") {
    if (resolvedOur !== "" && legacySku.toLowerCase() !== resolvedOur.toLowerCase()) {
      vendorPart = legacySku;
      mpn = legacySku;
    } else {
      mpn = legacySku;
      vendorPart = legacySku;
    }
  }

  vendorPart = ensureVendorCatalogFromMpn({
    ourPartNumber: resolvedOur,
    mpn,
    vendorPartNumber: vendorPart,
    legacySku,
  });

  return {
    extracted_our_part_number: resolvedOur !== "" ? resolvedOur : null,
    extracted_mpn: isLabor ? null : (mpn !== "" ? mpn : null),
    extracted_vendor_part_number: vendorPart !== "" ? vendorPart : null,
    extracted_part_number: isLabor ? (vendorPart !== "" ? vendorPart : null) : (mpn !== "" ? mpn : null),
    finished_good_sku: resolvedFinishedGood !== "" ? resolvedFinishedGood : null,
  };
}

export function formatApInvoiceSkuTranslation(params: {
  sku: string;
  extractedPartNumber: string | null | undefined;
  matchMethod: string | null | undefined;
}): string | null {
  const sku = params.sku.trim();
  const extracted = (params.extractedPartNumber ?? "").trim();
  if (extracted === "") return null;
  const same = sku !== "" && extracted.toLowerCase() === sku.toLowerCase();
  if (params.matchMethod === "unmapped") {
    return `MPN ${extracted} is not on the AML and was not mapped to a hub stock keeping unit.`;
  }
  if (params.matchMethod === "aml_mismatch") {
    const skuLabel = sku !== "" ? sku : "this line";
    return `MPN ${extracted} is not an approved manufacturer part for ${skuLabel} on the AML.`;
  }
  if (same) return null;
  const via =
    params.matchMethod === "aml"
      ? "AML"
      : params.matchMethod === "description"
        ? "the line description"
        : params.matchMethod === "purchase_order"
          ? "the linked purchase order"
          : "hub matching";
  return `MPN ${extracted} maps to ${sku} via ${via}.`;
}

export function apInvoiceSkuTranslationIsAlert(matchMethod: string | null | undefined): boolean {
  return matchMethod === "unmapped" || matchMethod === "aml_mismatch";
}

export function apInvoiceLineOurSku(line: {
  sku: string;
  extracted_our_part_number?: string | null;
  sku_match_method?: string | null;
}): string {
  const extractedOur = (line.extracted_our_part_number ?? "").trim();
  if (extractedOur !== "") return extractedOur;
  const sku = line.sku.trim();
  if (sku === "") return "";
  if (line.sku_match_method === "unmapped") return "";
  return sku;
}

/** @deprecated Use apInvoiceLineOurSku */
export function apInvoiceLineCpn(line: {
  sku: string;
  extracted_part_number?: string | null;
  sku_match_method?: string | null;
}): string {
  return apInvoiceLineOurSku(line);
}

export function apInvoiceLineMpn(line: {
  extracted_mpn?: string | null;
}): string {
  return (line.extracted_mpn ?? "").trim();
}

export function apInvoiceLineVendorPart(line: {
  extracted_vendor_part_number?: string | null;
}): string {
  return (line.extracted_vendor_part_number ?? "").trim();
}

export function apInvoiceLineFinishedGoodSku(line: {
  finished_good_sku?: string | null;
  sku?: string;
  extracted_our_part_number?: string | null;
  description?: string | null;
}): string {
  const explicit = (line.finished_good_sku ?? "").trim();
  if (explicit !== "") return explicit;
  const ourSku = apInvoiceLineOurSku({
    sku: line.sku ?? "",
    extracted_our_part_number: line.extracted_our_part_number,
  });
  if (isManufacturingLaborSku(ourSku)) {
    return parseFinishedGoodSkuFromLaborDescription(line.description) ?? "";
  }
  return "";
}

export function apInvoiceLineShowsFinishedGoodField(line: {
  finished_good_sku?: string | null;
  sku?: string;
  extracted_our_part_number?: string | null;
  description?: string | null;
}): boolean {
  const ourSku = apInvoiceLineOurSku({
    sku: line.sku ?? "",
    extracted_our_part_number: line.extracted_our_part_number,
  });
  return isManufacturingLaborSku(ourSku) || (line.finished_good_sku ?? "").trim() !== "";
}

export function apInvoiceLineHasPartNumbers(line: {
  sku: string;
  extracted_mpn?: string | null;
  extracted_vendor_part_number?: string | null;
  extracted_our_part_number?: string | null;
  extracted_part_number?: string | null;
}): boolean {
  return (
    apInvoiceLineOurSku(line) !== "" ||
    apInvoiceLineMpn(line) !== "" ||
    apInvoiceLineVendorPart(line) !== "" ||
    (line.extracted_our_part_number ?? "").trim() !== ""
  );
}

export function apInvoiceLinePrimaryMpnForTranslation(line: {
  extracted_mpn?: string | null;
  extracted_part_number?: string | null;
}): string | null {
  const mpn = apInvoiceLineMpn(line);
  return mpn !== "" ? mpn : null;
}

export interface TestedManufacturerPart {
  tested_mpn: string | null;
  tested_sku: string | null;
  sku_match_method: string | null;
}

/** Values from the last completed manufacturer-part check, including a line just loaded from the server. */
export function testedManufacturerPartFromLoadedLine(line: {
  sku: string;
  extracted_our_part_number?: string | null;
  extracted_mpn?: string | null;
  sku_match_method?: string | null;
}): TestedManufacturerPart {
  const mpn = apInvoiceLineMpn(line);
  const sku = apInvoiceLineOurSku(line);
  return {
    tested_mpn: mpn === "" ? null : mpn,
    tested_sku: sku === "" ? null : sku,
    sku_match_method: line.sku_match_method ?? null,
  };
}

/** True when the stock keeping unit or manufacturer part has changed since the last check. */
export function manufacturerPartInputsDifferFromTest(line: {
  sku: string;
  extracted_our_part_number?: string | null;
  sku_match_method?: string | null;
  extracted_mpn?: string | null;
  tested_mpn?: string | null;
  tested_sku?: string | null;
}): boolean {
  return (
    apInvoiceLineMpn(line) !== (line.tested_mpn ?? "").trim() ||
    apInvoiceLineOurSku(line) !== (line.tested_sku ?? "").trim()
  );
}

/** Warning copy from the last check. Typing in the field does not change this. */
export function apInvoiceTestedSkuTranslation(line: {
  tested_sku?: string | null;
  tested_mpn?: string | null;
  sku_match_method?: string | null;
}): string | null {
  return formatApInvoiceSkuTranslation({
    sku: (line.tested_sku ?? "").trim(),
    extractedPartNumber: line.tested_mpn,
    matchMethod: line.sku_match_method,
  });
}

export function applyManufacturerPartTestResult<T extends TestedManufacturerPart>(
  line: T,
  tested: { sku: string; mpn: string; matchMethod: string | null },
): T {
  const sku = tested.sku.trim();
  const mpn = tested.mpn.trim();
  return {
    ...line,
    tested_sku: sku === "" ? null : sku,
    tested_mpn: mpn === "" ? null : mpn,
    sku_match_method: tested.matchMethod,
  };
}

/** @deprecated Use apInvoiceLineMpn */
export function apInvoiceLinePrintedSku(line: {
  sku: string;
  extracted_part_number?: string | null;
}): string {
  const extracted = (line.extracted_part_number ?? "").trim();
  if (extracted !== "") return extracted;
  return line.sku.trim();
}
